import { lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { RequireAuth, RequirePermission } from "@/features/auth/RequireAuth";
import LoginPage from "@/features/auth/LoginPage";
import ForgotPasswordPage from "@/features/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/features/auth/ResetPasswordPage";

const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const ArtistListPage = lazy(() => import("@/features/artists/ArtistListPage"));
const ArtistFormPage = lazy(() => import("@/features/artists/ArtistFormPage"));
const ExhibitionListPage = lazy(() => import("@/features/exhibitions/ExhibitionListPage"));
const ExhibitionFormPage = lazy(() => import("@/features/exhibitions/ExhibitionFormPage"));
const NewsListPage = lazy(() => import("@/features/news/NewsListPage"));
const NewsFormPage = lazy(() => import("@/features/news/NewsFormPage"));
const PublicationListPage = lazy(() => import("@/features/publications/PublicationListPage"));
const PublicationFormPage = lazy(() => import("@/features/publications/PublicationFormPage"));
// One menu entry, list and form per strand — an editor lands on the strand they
// picked instead of choosing it again inside a shared screen.
const ArtResidencyListPage = lazy(() => import("@/features/practices/ArtResidencyListPage"));
const ArtResidencyFormPage = lazy(() => import("@/features/practices/ArtResidencyFormPage"));
const ArtFairsListPage = lazy(() => import("@/features/practices/ArtFairsListPage"));
const ArtFairsFormPage = lazy(() => import("@/features/practices/ArtFairsFormPage"));
const PartnershipsListPage = lazy(() => import("@/features/practices/PartnershipsListPage"));
const PartnershipsFormPage = lazy(() => import("@/features/practices/PartnershipsFormPage"));
const ArtEducationListPage = lazy(() => import("@/features/practices/ArtEducationListPage"));
const ArtEducationFormPage = lazy(() => import("@/features/practices/ArtEducationFormPage"));
const StoryPage = lazy(() => import("@/features/story/StoryPage"));
const PageIntrosPage = lazy(() => import("@/features/page-intros/PageIntrosPage"));
const MediaLibraryPage = lazy(() => import("@/features/media/MediaLibraryPage"));
const CarouselPage = lazy(() => import("@/features/carousel/CarouselPage"));
const TaxonomyPage = lazy(() => import("@/features/taxonomy/TaxonomyPage"));
const SubscribersPage = lazy(() => import("@/features/subscribers/SubscribersPage"));
const ContactPage = lazy(() => import("@/features/contact/ContactPage"));
const SeoPage = lazy(() => import("@/features/seo/SeoPage"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const UsersPage = lazy(() => import("@/features/users/UsersPage"));
const RolesPage = lazy(() => import("@/features/roles/RolesPage"));
const AuditLogsPage = lazy(() => import("@/features/audit-logs/AuditLogsPage"));
const ProfilePage = lazy(() => import("@/features/profile/ProfilePage"));

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: "artists",
        element: (
          <RequirePermission resource="ARTISTS" action="READ">
            <ArtistListPage />
          </RequirePermission>
        ),
      },
      {
        path: "artists/:id",
        element: (
          <RequirePermission resource="ARTISTS" action="READ">
            <ArtistFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "exhibitions",
        element: (
          <RequirePermission resource="EXHIBITIONS" action="READ">
            <ExhibitionListPage />
          </RequirePermission>
        ),
      },
      {
        path: "exhibitions/:id",
        element: (
          <RequirePermission resource="EXHIBITIONS" action="READ">
            <ExhibitionFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "news",
        element: (
          <RequirePermission resource="NEWS" action="READ">
            <NewsListPage />
          </RequirePermission>
        ),
      },
      {
        path: "news/:id",
        element: (
          <RequirePermission resource="NEWS" action="READ">
            <NewsFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "publications",
        element: (
          <RequirePermission resource="PUBLICATIONS" action="READ">
            <PublicationListPage />
          </RequirePermission>
        ),
      },
      {
        path: "publications/:id",
        element: (
          <RequirePermission resource="PUBLICATIONS" action="READ">
            <PublicationFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-residency",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtResidencyListPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-residency/:id",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtResidencyFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-fairs",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtFairsListPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-fairs/:id",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtFairsFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/partnerships",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <PartnershipsListPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/partnerships/:id",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <PartnershipsFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-education",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtEducationListPage />
          </RequirePermission>
        ),
      },
      {
        path: "practices/art-education/:id",
        element: (
          <RequirePermission resource="PRACTICES" action="READ">
            <ArtEducationFormPage />
          </RequirePermission>
        ),
      },
      {
        path: "our-story",
        element: (
          <RequirePermission resource="PAGES" action="READ">
            <StoryPage />
          </RequirePermission>
        ),
      },
      {
        path: "page-headings",
        element: (
          <RequirePermission resource="PAGES" action="READ">
            <PageIntrosPage />
          </RequirePermission>
        ),
      },
      {
        path: "media",
        element: (
          <RequirePermission resource="MEDIA" action="READ">
            <MediaLibraryPage />
          </RequirePermission>
        ),
      },
      {
        path: "carousel",
        element: (
          <RequirePermission resource="CAROUSEL" action="READ">
            <CarouselPage />
          </RequirePermission>
        ),
      },
      {
        path: "taxonomy",
        element: (
          <RequirePermission resource="TAXONOMY" action="READ">
            <TaxonomyPage />
          </RequirePermission>
        ),
      },
      {
        path: "subscribers",
        element: (
          <RequirePermission resource="SUBSCRIBERS" action="READ">
            <SubscribersPage />
          </RequirePermission>
        ),
      },
      {
        path: "contact",
        element: (
          <RequirePermission resource="CONTACT_SUBMISSIONS" action="READ">
            <ContactPage />
          </RequirePermission>
        ),
      },
      {
        path: "seo",
        element: (
          <RequirePermission resource="SEO" action="READ">
            <SeoPage />
          </RequirePermission>
        ),
      },
      {
        path: "settings",
        element: (
          <RequirePermission resource="SETTINGS" action="READ">
            <SettingsPage />
          </RequirePermission>
        ),
      },
      {
        path: "users",
        element: (
          <RequirePermission resource="USERS" action="READ">
            <UsersPage />
          </RequirePermission>
        ),
      },
      {
        path: "roles",
        element: (
          <RequirePermission resource="ROLES" action="READ">
            <RolesPage />
          </RequirePermission>
        ),
      },
      {
        path: "audit-logs",
        element: (
          <RequirePermission resource="AUDIT_LOGS" action="READ">
            <AuditLogsPage />
          </RequirePermission>
        ),
      },
      { path: "profile", element: <ProfilePage /> },
    ],
  },
]);
